import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createEntityManagementUnit,
  deleteManagementUnit, 
  getEntityManagementUnits, 
  getManagementUnitTypes, 
  updateManagementUnit
} from "../../api/managementUnits";
import {
  CreateManagementUnitRequest,
  ManagementUnitResponse,
  UpdateManagementUnitRequest,
} from "../../types/model/managementUnit";

export const useEntityManagementUnits = (
  entityIdStr?: string,
) => {
  const queryClient = useQueryClient();
  const entityId = Number(entityIdStr);

  const unitsQuery = useQuery({
    queryKey: ["entity", entityId, "managementUnits"],
    queryFn: async () => {
      const [units, types] = await Promise.all([
        getEntityManagementUnits(entityId),
        getManagementUnitTypes(),
      ]);

      return units.map((unit) => {
        const type = types.find((t) => t.id === unit.unitTypeId);

        return {
          ...unit,
          managementUnitTypeI18nLabel: type?.i18nLabel ?? "",
          managementUnitTypeType: type?.type ?? "",
        } as ManagementUnitResponse;
      });
    },
    enabled: !!entityId,
  });

  const create = useMutation({
    mutationFn: (values: CreateManagementUnitRequest) =>
      createEntityManagementUnit(entityId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["entity", entityId, "managementUnits"],
      });
    },
  });

  const update = useMutation({
    mutationFn: ({
      managementUnitId,
      values,
    }: {
      managementUnitId: number;
      values: UpdateManagementUnitRequest;
    }) => updateManagementUnit(managementUnitId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["entity", entityId, "managementUnits"],
      });
    },
  });

  const remove = useMutation({
    mutationFn: (managementUnitId: number) =>
      deleteManagementUnit(managementUnitId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["entity", entityId, "managementUnits"],
      });
    },
  });

  return {
    managementUnitsData: unitsQuery.data,
    isPending: unitsQuery.isPending,
    isFetching: unitsQuery.isFetching,
    isError: unitsQuery.isError || create.isError || update.isError || remove.isError,
    error: unitsQuery.error || create.error || update.error || remove.error,
    refetch: unitsQuery.refetch,

    create: create.mutateAsync,
    update: update.mutateAsync,
    remove: remove.mutateAsync,
  };
};